import React from 'react';
import {
    ExtendedSelect,
    InputText,
    DatePicker,
    WarningModal,
    LoaderBtn,
    CustomSwitch,
    PhoneInput,
    TextLink,
    PersonalDataTab,
    Loader,
    SexRadios,
    Textarea,
} from 'components';
import {
    ApplicantPassport,
    ApplicantOtherIdentity,
    ExamStatus,
    IExamApplicationDetails,
} from '../../../api/ApiClient';
import { ApplicantDraftPhoneModal } from '../../../components';
import { Sex, IdentityDocType } from 'components/enums';
import { IRootState, Dispatch } from '../../../redux/store/configureStore';
import { Form, Col, Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { RegExpConst, formatPhoneNumber, RegExpErrorsText } from '_helpers';
import styles from './ProfileInfoTabContainer.module.scss';
import moment from 'moment';
import cn from 'classnames';
import _ from 'lodash';

const ProfileInfoTabContainer = ({
    applicantId: applicantIdProps,
    apiError,
    onPush,
    createExamApplicationDraft,
    updateExamApplicationDraft,
    deleteExamApplicationDraft,
    applicationDetails,
    examId,
    phone,
    applicantDetail: applicantDetailProps,
    getApplicantDetail,
    onCancel,
    getApplicantByPhone,
    clearApplicantDetail,
    getApplicantIdentityDoc,
    applicantIdentityDoc,
    examApplicationId,
    fetchExamApplicationDetails,
}: Props) => {
    const initData: IDraft = {
        identityDocType: IdentityDocType.Passport,
        phone: phone || '',
        lastName: '',
        firstName: '',
        middleName: '',
        sex: Sex.Male,
        birthDate: null,
        tempPassword: '',
        examId: examId || '',
        email: '',
    };
    const initPassport: IInitPassport = {
        serialNumber: '',
        issuedBy: '',
        dateOfIssue: undefined,
        registrationAddress: '',
    };
    const initOtherIdentity = {
        title: '',
        data: '',
    };
    const [data, setData] = React.useState(initData);
    const [validErrors, setValidErrors] = React.useState<IInitErrors>({});
    const [passport, setPassport] = React.useState(initPassport);
    const [otherIdentity, setOtherIdentity] = React.useState(initOtherIdentity);
    const [disabled, setDisabled] = React.useState(false);
    const [isExpert, setIsExpert] = React.useState(false);

    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [showAddApplicantModal, setShowAddApplicantModal] = React.useState(false);
    const [showWarningModal, setShowWarningModal] = React.useState(false);
    const [processing, setProcessing] = React.useState(false);
    const [fetching, setFetching] = React.useState(false);

    const params = new URLSearchParams(window.location.search);
    const applicantId = applicantIdProps || params.get('applicantId');
    const editableApplicant = Boolean(!applicantId || (applicationDetails && applicationDetails.editableApplicant));
    const applicantDetail = React.useMemo(() => (applicationDetails ? applicationDetails.applicant : applicantDetailProps), [applicationDetails, applicantDetailProps]);

    const docTypeOptions = [
        {
            label: 'Общегражданский паспорт',
            value: IdentityDocType.Passport,
        },
        {
            label: 'Другое',
            value: IdentityDocType.Other,
        },
    ];

    React.useEffect(() => {
        if (!applicationDetails && !applicantDetailProps) {
            setData(initData);
            setPassport(initPassport);
        }
    }, [applicantDetailProps, applicationDetails]);

    React.useEffect(() => {
        const fetch = async () => {
            setFetching(true);
            if (examApplicationId) {
                await fetchExamApplicationDetails(examApplicationId);
            }
            setFetching(false);
        };

        fetch();
    }, [examApplicationId]);

    React.useEffect(() => {
        const fetch = async () => {
            if (applicantId) {
                setFetching(true);
                await getApplicantDetail({ applicantId });
                await getApplicantIdentityDoc(applicantId);
                setFetching(false);
            }
        };

        fetch();
    }, [examId, applicantId]);

    React.useEffect(() => {
        if (applicantIdentityDoc && !showAddApplicantModal) {
            if (applicantIdentityDoc.passport) {
                setPassport(applicantIdentityDoc.passport);
            } else if (applicantIdentityDoc.otherIdentity) {
                setOtherIdentity(applicantIdentityDoc.otherIdentity);
            }

            setData({ ...data, identityDocType: applicantIdentityDoc.identityDocType });
        }
    }, [applicantIdentityDoc]);

    React.useEffect(() => {
        if (apiError && apiError.failures) {
            const errors = apiError.failures;

            for (const key in errors) {
                if (Object.prototype.hasOwnProperty.call(errors, key) && errors[key]) {
                    const error = errors[key].join(',');
                    const newErrors = validErrors;
                    newErrors[key] = error;

                    setValidErrors({ ...newErrors });
                }
            }
        } else {
            setValidErrors({});
        }
    }, [apiError]);

    React.useEffect(() => {
        if (!showAddApplicantModal && applicationDetails) {
            const { applicant } = applicationDetails;
            setData({
                ...applicant,
                identityDocType: applicationDetails.applicantIdentityDoc.identityDocType,
                middleName: applicant.middleName || '',
                examId: applicationDetails.exam.id,
                birthDate: applicant.birthDate || null,
                tempPassword: '',
            });

            setIsExpert(applicationDetails.applicant.isExpert);

            if (applicationDetails.exam.status !== ExamStatus.IsApplying) {
                setDisabled(true);
            }

            if (applicationDetails.applicantIdentityDoc.passport) {
                setPassport(applicationDetails.applicantIdentityDoc.passport);
            } else if (applicationDetails.applicantIdentityDoc.otherIdentity) {
                setOtherIdentity(applicationDetails.applicantIdentityDoc.otherIdentity);
            }
        }
    }, [applicationDetails]);

    React.useEffect(() => {
        if (applicantDetailProps && !applicationDetails) {
            setData({ ...data, ...applicantDetailProps });
        }
    }, [applicantDetailProps]);

    const onChange = (name: string, value: string) => {
        setData({ ...data, [name]: value });
        setValidErrors({ ...validErrors, [name]: '' });
    };

    const checkFieldsErrors = (fields: IDraft) => _.mapValues(fields, (val, key) => {
        const test = (field: string, regexp: RegExp) => (!regexp.test(field));

        if (key === 'examId') return '';
        if (key === 'birthDate' && !val) return 'Обязательное поле для заполнения';
        if (key === 'dateOfIssue' && !val) return 'Обязательное поле для заполнения';
        if ((key === 'firstName' || key === 'lastName' || key === 'middleName') && typeof val === 'string') {
            const error = test(val, RegExpConst.NAME);

            if (error) {
                return RegExpErrorsText.NAME;
            }

            return '';
        }
        if (key === 'phone' && typeof val === 'string') {
            const error = test(val, RegExpConst.PHONE);

            if (error) {
                return RegExpErrorsText.PHONE;
            }

            return '';
        }
        if (key === 'email' && typeof val === 'string') {
            if (val.length > 0) {
                const error = test(val, RegExpConst.EMAIL);

                if (error) {
                    return RegExpErrorsText.EMAIL;
                }
            }

            return '';
        }
        if (key === 'tempPassword' && typeof val === 'string') {
            if (!applicantId) {
                const error = test(val, RegExpConst.PASSWORD);

                if (error) {
                    return RegExpErrorsText.PASSWORD;
                }
            }

            return '';
        }
        if (val === '') return 'Обязательное поле для заполнения';

        return '';
    });

    const isValidFormData = (fieldsError: IInitErrors): boolean => _.values(fieldsError).every(e => e.length === 0);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (disabled) return;

        setProcessing(true);

        let appDetails: IExamApplicationDetails | null = null;

        const docData = data.identityDocType === IdentityDocType.Passport ? passport : otherIdentity;

        const errData = {
            ...data,
            ...docData,
        };

        const errors = checkFieldsErrors(errData) as IInitErrors;
        setValidErrors(errors);

        if (isValidFormData(errors) && data.birthDate) {
            if (data.identityDocType === IdentityDocType.Passport && passport.dateOfIssue) {
                const body = {
                    passport: new ApplicantPassport({
                        ...passport,
                        dateOfIssue: passport.dateOfIssue,
                    }),
                    ...data,
                    tempPassword: data.tempPassword || '123456',
                    birthDate: data.birthDate,
                    isExpert,
                };

                if (applicationDetails) {
                    appDetails = await updateExamApplicationDraft({
                        examApplicationId: applicationDetails.examApplicationId,
                        body,
                    });
                } else {
                    appDetails = await createExamApplicationDraft(body);
                }
            }

            if (data.identityDocType === IdentityDocType.Other) {
                const body = {
                    otherIdentity: new ApplicantOtherIdentity(otherIdentity),
                    ...data,
                    tempPassword: data.tempPassword || '123456',
                    birthDate: data.birthDate,
                    isExpert,
                };

                if (applicationDetails) {
                    appDetails = await updateExamApplicationDraft({
                        examApplicationId: applicationDetails.examApplicationId,
                        body,
                    });
                } else {
                    appDetails = await createExamApplicationDraft(body);
                }
            }

            if (appDetails) {
                setProcessing(false);

                const urlParams = new URLSearchParams(window.location.search);
                const paramExamId = urlParams.get('examId');

                onPush(`/exam-draft/${appDetails.examApplicationId}?tab=doc${paramExamId ? `&examId=${paramExamId}` : ''}`);
            }
        }
        setProcessing(false);
    };

    const handlePassportDateChange = (val: string, momentDate: moment.Moment) => {
        setPassport({ ...passport, dateOfIssue: momentDate });
        setValidErrors({ ...validErrors, dateOfIssue: '' });
    };

    const onDelete = async () => {
        if (applicationDetails) {
            const res = await deleteExamApplicationDraft(applicationDetails.examApplicationId);

            if (res) {
                setShowDeleteModal(false);

                if (onCancel) {
                    onCancel();
                }
            }
        }
    };

    const onChangeIsExpert = () => {
        if (!disabled) {
            setShowWarningModal(true);
        }
    };

    const onHideApplicantDraftPhoneModal = () => {
        setShowAddApplicantModal(false);
        clearApplicantDetail();
    };

    const renderProfile = () => (editableApplicant || showAddApplicantModal ? (
        <>
            <Form.Group className={`row ${styles.switch}`}>
                <Col sm="2" className="p-0">
                    <Form.Label column>
                        ЯВЛЯЕТСЯ ЭКСПЕРТОМ
                    </Form.Label>
                </Col>
                <Col sm="10">
                    <CustomSwitch
                        onClick={onChangeIsExpert}
                        name="isExpert"
                        value={isExpert || false}
                        status="default"
                    />
                </Col>
            </Form.Group>

            <PhoneInput
                value={applicantId ? (formatPhoneNumber(data.phone) || data.phone) : data.phone}
                labelTitle="ТЕЛЕФОН"
                fieldFailures={validErrors}
                labelColSize={2}
                inputColSize={3}
                phoneHelperClassName={cn(styles.phone_input__helper, styles.phone_input__helper_disabled)}
                inputClassNames={styles.phone_field}
                onChange={onChange}
                withDarkLabel
                disabled
            >
                {examId && phone ? (
                    <TextLink
                        text="Изменить номер"
                        className={styles.phone_change_button}
                        onClick={() => setShowAddApplicantModal(true)}
                    />
                ) : null}
            </PhoneInput>

            <InputText
                withDarkLabel
                name="email"
                value={data.email || ''}
                labelTitle="ПОЧТА"
                labelColSize={2}
                inputColSize={4}
                fieldFailures={validErrors}
                inputClassNames={styles.field}
                disabled={disabled}
                onChange={onChange}
            />

            <SexRadios
                sex={data.sex}
                disabled={disabled}
                validErrors={validErrors}
                onClick={(newSex) => {
                    setData({ ...data, sex: newSex as Sex });
                    setValidErrors({ ...validErrors, sex: '' });
                }}
            />

            <InputText
                withDarkLabel
                name="lastName"
                value={data.lastName}
                labelTitle="ФАМИЛИЯ"
                labelColSize={2}
                fieldFailures={validErrors}
                inputClassNames={styles.field}
                disabled={disabled}
                onChange={onChange}
            />
            <InputText
                withDarkLabel
                name="firstName"
                value={data.firstName}
                labelTitle="ИМЯ"
                labelColSize={2}
                fieldFailures={validErrors}
                inputClassNames={styles.field}
                disabled={disabled}
                onChange={onChange}
            />
            <InputText
                withDarkLabel
                name="middleName"
                value={data.middleName}
                labelTitle="ОТЧЕСТВО"
                labelColSize={2}
                fieldFailures={validErrors}
                inputClassNames={styles.field}
                disabled={disabled}
                onChange={onChange}
            />
            <Form.Group className={`row ${styles.selectContainer} mb-5`}>
                <Col sm="2" className="p-0">
                    <Form.Label column>
                        ДАТА РОЖДЕНИЯ
                    </Form.Label>
                </Col>
                <Col sm="8" className={styles.colContainer}>
                    <DatePicker
                        initialDate={data.birthDate || undefined}
                        disabled={disabled}
                        isInvalid={validErrors.birthDate}
                        onChange={(val, newMoment) => {
                            setData({ ...data, birthDate: newMoment });
                            setValidErrors({ ...validErrors, birthDate: '' });
                        }}
                    />
                </Col>
            </Form.Group>

            <ApplicantDraftPhoneModal
                show={showAddApplicantModal}
                onHide={onHideApplicantDraftPhoneModal}
                onPush={onPush}
                getApplicantByPhone={getApplicantByPhone}
                applicantDetail={applicantDetail}
                examId={examId}
            />
        </>
    ) : (
            <PersonalDataTab
                personalData={applicantDetail}
                rowClassName={styles.personalRowClassName}
                cardClassName="mb-5"
                cardCategory={null}
                cardTitle={null}
                withoutPaddingRows
                withoutPadding
                withoutRegion
                withoutEmail
                withoutPhone
            />
        ));

    if (fetching && !showAddApplicantModal) return <Loader fullHeight />;

    return (
        <section>
            <Form onSubmit={onSubmit} autoComplete="new-password">
                {disabled && (
                    <span className="text-danger mb-4 d-block">Экзамен должен быть в статусе «Идет запись».</span>
                )}

                {renderProfile()}

                <Form.Group className={`row ${styles.selectContainer}`}>
                    <Col sm="2" className="p-0">
                        <Form.Label column>
                            ТИП ДОКУМЕНТА
                        </Form.Label>
                    </Col>
                    <Col sm="10" className={styles.colContainer}>
                        <ExtendedSelect
                            name="identityDocType"
                            value={data.identityDocType}
                            options={docTypeOptions}
                            disabled={disabled}
                            onChange={e => onChange(e.name, e.value as IdentityDocType)}
                        />
                        <Form.Control.Feedback type="invalid">{validErrors.identityDocType}</Form.Control.Feedback>
                    </Col>
                </Form.Group>
                {data.identityDocType === IdentityDocType.Passport ? (
                    <>
                        <InputText
                            withDarkLabel
                            name="serialNumber"
                            value={passport.serialNumber}
                            onChange={(name, value) => {
                                setPassport({ ...passport, serialNumber: value });
                                setValidErrors({ ...validErrors, serialNumber: '' });
                            }}
                            labelTitle="СЕРИЯ/НОМЕР"
                            labelColSize={2}
                            disabled={disabled}
                            inputClassNames={styles.field}
                            fieldFailures={validErrors}
                        />
                        <Form.Group className={`row ${styles.selectContainer}`}>
                            <Col sm="2" className="p-0">
                                <Form.Label column className="pt-2">
                                    КЕМ ВЫДАН
                                </Form.Label>
                            </Col>
                            <Col sm="10" className={styles.colContainer}>
                                <Textarea
                                    name='issuedBy'
                                    validErrors={validErrors}
                                    value={passport.issuedBy}
                                    disabled={disabled}
                                    onChange={(issuedBy) => {
                                        setPassport({ ...passport, issuedBy });
                                        setValidErrors({ ...validErrors, issuedBy: '' });
                                    }}
                                    resize
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group className={`row ${styles.selectContainer}`}>
                            <Col sm="2" className="p-0">
                                <Form.Label column>
                                    ДАТА ВЫДАЧИ
                                </Form.Label>
                            </Col>
                            <Col sm="6" className={styles.colContainer}>
                                <DatePicker
                                    from={-30}
                                    to={0}
                                    onChange={handlePassportDateChange}
                                    disabled={disabled}
                                    initialDate={passport.dateOfIssue}
                                    isInvalid={validErrors.dateOfIssue}
                                />
                            </Col>
                        </Form.Group>
                        <Form.Group className={`row ${styles.selectContainer}`}>
                            <Col sm="2" className="p-0">
                                <Form.Label column className="pt-2">
                                    АДРЕС РЕГИСТРАЦИИ
                                </Form.Label>
                            </Col>
                            <Col sm="10" className={styles.colContainer}>
                                <Textarea
                                    name='registrationAddress'
                                    validErrors={validErrors}
                                    value={passport.registrationAddress}
                                    disabled={disabled}
                                    onChange={(registrationAddress) => {
                                        setPassport({ ...passport, registrationAddress });
                                        setValidErrors({ ...validErrors, registrationAddress: '' });
                                    }}
                                    resize
                                />
                            </Col>
                        </Form.Group>
                    </>
                ) : (
                        <>
                            <InputText
                                withDarkLabel
                                name="title"
                                value={otherIdentity.title}
                                onChange={(name, value) => {
                                    setOtherIdentity({ ...otherIdentity, title: value });
                                    setValidErrors({ ...validErrors, title: '' });
                                }}
                                labelTitle="НАЗВАНИЕ ДОКУМЕНТА"
                                labelColSize={2}
                                disabled={disabled}
                                fieldFailures={validErrors}
                                inputClassNames={styles.field}
                                placeholder="Укажите название документа"
                            />
                            <Form.Group className={`row ${styles.selectContainer}`}>
                                <Col sm="2" className="p-0">
                                    <Form.Label column className="pt-2">
                                        ДАННЫЕ ДОКУМЕНТА
                                    </Form.Label>
                                </Col>
                                <Col sm="10" className={styles.colContainer}>
                                    <Textarea
                                        name='data'
                                        placeholder="Укажите полную информацию о документе, включая серию/номер, дату выдачи, кем выдан итп."
                                        validErrors={validErrors}
                                        value={otherIdentity.data}
                                        disabled={disabled}
                                        onChange={(data) => {
                                            setOtherIdentity({ ...otherIdentity, data });
                                            setValidErrors({ ...validErrors, data: '' });
                                        }}
                                        resize
                                    />
                                </Col>
                            </Form.Group>
                        </>
                    )}
                {!applicantId && (
                    <InputText
                        withDarkLabel
                        name="tempPassword"
                        value={data.tempPassword}
                        labelTitle="ВРЕМЕННЫЙ ПАРОЛЬ"
                        labelColSize={2}
                        fieldFailures={validErrors}
                        inputClassNames={styles.field}
                        disabled={disabled}
                        onChange={onChange}
                    />
                )}
                <Form.Group className="row mb-0 mt-5">
                    <Col sm={{ offset: 2 }}>
                        <LoaderBtn loading={processing} disabled={processing || disabled} type="submit">Следующий шаг</LoaderBtn>
                        {onCancel ? (<Button className="ml-4" variant="outline-secondary" onClick={onCancel}>{disabled ? 'Назад' : 'Отмена'}</Button>) : null}
                    </Col>
                </Form.Group>
            </Form>

            {applicantId && !examId && (
                <Form.Group className="row mb-0 mt-2">
                    <Col sm={{ span: 3, offset: 2 }}>
                        <span className={cn(styles.deleteButton, 'text-danger')} onClick={() => setShowDeleteModal(true)}>Удалить</span>
                    </Col>
                </Form.Group>
            )}
            <WarningModal
                title="Удалить черновик?"
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onAccess={onDelete}
            />
            <WarningModal
                title="Профиль эксперта не найден"
                show={showWarningModal}
                onHide={() => setShowWarningModal(false)}
            >
                <span>
                    Для регистрации данного соискателя в качестве эксперта, обратитесь в службу поддержки АС СПКФР:
                    <a href="mailto:support@spkfr.ru" style={{ color: 'var(--primary)' }}> support@spkfr.ru</a>
                </span>
            </WarningModal>
        </section>
    );
};

const mapState = (state: IRootState) => ({
    apiError: state.errors.error,
    applicantDetail: state.applicants.applicantDetail,
    applicationDetails: state.examApplications.applicationDetails,
    applicantIdentityDoc: state.applicants.applicantIdentityDoc,
});

const mapDispatch = (dispatch: Dispatch) => ({
    onPush: (key: string) => dispatch(push(key)),
    getApplicantDetail: dispatch.applicants.getApplicantAsync,
    getApplicantIdentityDoc: dispatch.applicants.getApplicantIdentityDocAsync,
    clearApplicantDetail: dispatch.applicants.clearApplicantDetail,
    getApplicant: dispatch.applicants.getApplicantAsync,
    fetchExamApplicationDetails: dispatch.examApplications.fetchExamApplicationDetailsAsync,
    createExamApplicationDraft: dispatch.examApplications.createExamApplicationDraftAsync,
    updateExamApplicationDraft: dispatch.examApplications.updateExamApplicationDraftAsync,
    deleteExamApplicationDraft: dispatch.examApplications.deleteExamApplicationDraftAsync,
    getApplicantByPhone: dispatch.applicants.getApplicantByPhoneAsync,
});

export default connect(mapState, mapDispatch)(ProfileInfoTabContainer);

interface IProps {
    applicantId?: string;
    examId?: string | null;
    phone?: string | null;
    examApplicationId?: string;
    onCancel?: () => void;
}

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
type Props = connectedProps & IProps;

interface IDraft {
    examId: string;
    identityDocType: IdentityDocType;
    phone: string;
    lastName: string;
    firstName: string;
    middleName: string;
    sex: Sex;
    birthDate: moment.Moment | null;
    tempPassword: string;
    email?: string;
}

interface IInitErrors {
    [key: string]: string;
}

interface IInitPassport {
    serialNumber: string;
    issuedBy: string;
    dateOfIssue: moment.Moment | undefined;
    registrationAddress: string;
}
